import React, { useState } from 'react';
import { 
  Search,
  Plus,
  User,
  Shield,
  ChevronDown,
  X,
  Mail
} from 'lucide-react';
import styles from './PeopleSettings.module.css';

export default function PeopleSettings() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({
    emails: '',
    role: 'reader',
    inviteToDefaultProject: false
  });
  
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [showCreateTeamForm, setShowCreateTeamForm] = useState(false);
  const [createTeamData, setCreateTeamData] = useState({
    name: '',
    description: '',
    defaultRole: 'member'
  });

  // Mock data - replace with actual API calls
  const teamMembers = [
    {
      id: '1',
      name: 'SiameseAI Inc',
      email: 'admin@siameseai.com',
      role: 'owner',
      isCurrentUser: true,
      avatar: 'S',
      type: 'user'
    },
    {
      id: '2',
      name: 'siameseai-service-account',
      email: 'service@siameseai.com',
      role: 'owner',
      isCurrentUser: false,
      avatar: 'S',
      type: 'service'
    }
  ];

  const teams = [
    {
      id: '1',
      name: 'Default Team',
      description: 'Default team for existing tenant',
      memberCount: 2,
      role: 'owner',
      createdAt: '2024-01-01'
    },
    {
      id: '2',
      name: 'Engineering Team',
      description: 'Core engineering and development team',
      memberCount: 5,
      role: 'admin',
      createdAt: '2024-02-01'
    }
  ];

  const roles = [
    { value: 'owner', label: 'Owner' },
    { value: 'admin', label: 'Admin' },
    { value: 'member', label: 'Member' },
    { value: 'reader', label: 'Reader' }
  ];

  const handleInvite = async () => {
    try {
      console.log('Inviting team members:', inviteData);
      // TODO: Implement API call to invite team members
      
      // Reset form and hide
      setInviteData({
        emails: '',
        role: 'reader',
        inviteToDefaultProject: false
      });
      setShowInviteForm(false);
      
      // Show success message
      console.log('Invites sent successfully');
    } catch (error) {
      console.error('Failed to send invites:', error);
    }
  };

  const handleCancelInvite = () => {
    setInviteData({
      emails: '',
      role: 'reader',
      inviteToDefaultProject: false
    });
    setShowInviteForm(false);
  };

  const handleCreateTeam = async () => {
    try {
      console.log('Creating team:', createTeamData);
      // TODO: Implement API call to create team
      
      // Reset form and hide
      setCreateTeamData({
        name: '',
        description: '',
        defaultRole: 'member'
      });
      setShowCreateTeamForm(false);
      
      // Show success message
      console.log('Team created successfully');
    } catch (error) {
      console.error('Failed to create team:', error);
    }
  };

  const handleCancelCreateTeam = () => {
    setCreateTeamData({
      name: '',
      description: '',
      defaultRole: 'member'
    });
    setShowCreateTeamForm(false);
  };

  const filteredMembers = teamMembers.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(teamSearchQuery.toLowerCase()) ||
    team.description.toLowerCase().includes(teamSearchQuery.toLowerCase())
  );

  const getRoleColor = (role) => {
    const roleColors = {
      'owner': 'hsl(var(--destructive))',
      'admin': 'hsl(var(--success))',
      'member': 'hsl(var(--primary))',
      'reader': 'hsl(var(--muted-foreground))'
    };
    return roleColors[role] || roleColors.reader;
  };

  const getRoleLabel = (role) => {
    return roles.find(r => r.value === role)?.label || role;
  };

  return (
    <div className={styles.peopleSettings}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1>People</h1>
          <p className={styles.description}>Manage team members and control access permissions</p>
        </div>
        <div className={styles.headerControls}>
          {/* Action buttons moved to individual sections */}
        </div>
      </div>

      {/* Manage Individuals Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Manage Individuals</h2>
            <p>Invite and manage individual team members</p>
          </div>
          <button 
            className={styles.sectionButton}
            onClick={() => setShowInviteForm(true)}
          >
            <Plus size={16} />
            <span>Invite</span>
          </button>
        </div>
        
        {/* Search Bar */}
        <div className={styles.searchContainer}>
          <div className={styles.searchBar}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Invite Form Card */}
        {showInviteForm && (
          <div className={styles.inviteCard}>
            <div className={styles.inviteHeader}>
              <h3>Invite team members</h3>
              <button 
                className={styles.closeButton}
                onClick={handleCancelInvite}
              >
                <X size={16} />
              </button>
            </div>
            
            <div className={styles.inviteForm}>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <label htmlFor="emails">Emails</label>
                  <input
                    type="text"
                    id="emails"
                    placeholder="jane@example.com, joe@example.com..."
                    value={inviteData.emails}
                    onChange={(e) => setInviteData({...inviteData, emails: e.target.value})}
                    className={styles.inputField}
                  />
                </div>
                
                <div className={styles.formField}>
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    value={inviteData.role}
                    onChange={(e) => setInviteData({...inviteData, role: e.target.value})}
                    className={styles.selectField}
                    style={{
                      fontFamily: "'Titillium Web', sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "hsl(var(--foreground))",
                      backgroundColor: "hsl(var(--background))"
                    }}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className={styles.checkboxField}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={inviteData.inviteToDefaultProject}
                    onChange={(e) => setInviteData({...inviteData, inviteToDefaultProject: e.target.checked})}
                    className={styles.checkbox}
                  />
                  <span>Invite to default project</span>
                </label>
              </div>
              
              <div className={styles.inviteActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={handleCancelInvite}
                >
                  Cancel
                </button>
                <button 
                  className={styles.inviteSubmitButton}
                  onClick={handleInvite}
                  disabled={!inviteData.emails.trim()}
                >
                  <Mail size={16} />
                  <span>Invite</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Team Members List */}
        <div className={styles.membersList}>
          {filteredMembers.map(member => (
            <div key={member.id} className={styles.memberRow}>
              <div className={styles.memberInfo}>
                <div className={styles.memberAvatar}>
                  {member.type === 'service' ? (
                    <Shield size={16} />
                  ) : (
                    <span>{member.avatar}</span>
                  )}
                </div>
                <div className={styles.memberDetails}>
                  <div className={styles.memberName}>
                    {member.name}
                    {member.isCurrentUser && (
                      <span className={styles.currentUserTag}>You</span>
                    )}
                  </div>
                  <div className={styles.memberEmail}>{member.email}</div>
                </div>
              </div>
              
              <div className={styles.memberActions}>
                <button className={styles.roleDropdown}>
                  <span style={{ color: getRoleColor(member.role) }}>
                    {getRoleLabel(member.role)}
                  </span>
                  <ChevronDown size={16} />
                </button>
                
                {member.isCurrentUser ? (
                  <button className={styles.leaveButton}>
                    Leave
                  </button>
                ) : (
                  <button className={styles.removeButton}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {filteredMembers.length === 0 && (
            <div className={styles.emptyState}>
              <User size={48} />
              <h3>No team members found</h3>
              <p>Try adjusting your search or invite new team members to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Manage Teams Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2>Manage Teams</h2>
            <p>Create and manage teams for better organization</p>
          </div>
          <button 
            className={styles.sectionButton}
            onClick={() => setShowCreateTeamForm(true)}
          >
            <Plus size={16} />
            <span>Create Team</span>
          </button>
        </div>
        
        {/* Search Bar */}
        <div className={styles.searchContainer}>
          <div className={styles.searchBar}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search teams..."
              value={teamSearchQuery}
              onChange={(e) => setTeamSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Create Team Form Card */}
        {showCreateTeamForm && (
          <div className={styles.inviteCard}>
            <div className={styles.inviteHeader}>
              <h3>Create new team</h3>
              <button 
                className={styles.closeButton}
                onClick={handleCancelCreateTeam}
              >
                <X size={16} />
              </button>
            </div>
            
            <div className={styles.inviteForm}>
              <div className={styles.formRow}>
                <div className={styles.formField}>
                  <label htmlFor="teamName">Team Name</label>
                  <input
                    type="text"
                    id="teamName"
                    placeholder="Enter team name..."
                    value={createTeamData.name}
                    onChange={(e) => setCreateTeamData({...createTeamData, name: e.target.value})}
                    className={styles.inputField}
                  />
                </div>
                
                <div className={styles.formField}>
                  <label htmlFor="defaultRole">Default Role</label>
                  <select
                    id="defaultRole"
                    value={createTeamData.defaultRole}
                    onChange={(e) => setCreateTeamData({...createTeamData, defaultRole: e.target.value})}
                    className={styles.selectField}
                    style={{
                      fontFamily: "'Titillium Web', sans-serif",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      color: "hsl(var(--foreground))",
                      backgroundColor: "hsl(var(--background))"
                    }}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className={styles.formField}>
                <label htmlFor="teamDescription">Description</label>
                <textarea
                  id="teamDescription"
                  placeholder="Enter team description..."
                  value={createTeamData.description}
                  onChange={(e) => setCreateTeamData({...createTeamData, description: e.target.value})}
                  className={styles.textareaField}
                  rows={3}
                />
              </div>
              
              <div className={styles.inviteActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={handleCancelCreateTeam}
                >
                  Cancel
                </button>
                <button 
                  className={styles.inviteSubmitButton}
                  onClick={handleCreateTeam}
                  disabled={!createTeamData.name.trim()}
                >
                  <Plus size={16} />
                  <span>Create Team</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Teams List */}
        <div className={styles.membersList}>
          {filteredTeams.map(team => (
            <div key={team.id} className={styles.memberRow}>
              <div className={styles.memberInfo}>
                <div className={styles.memberAvatar}>
                  <span>{team.name.charAt(0)}</span>
                </div>
                <div className={styles.memberDetails}>
                  <div className={styles.memberName}>
                    {team.name}
                  </div>
                  <div className={styles.memberEmail}>{team.description}</div>
                </div>
              </div>
              
              <div className={styles.memberActions}>
                <button className={styles.roleDropdown}>
                  <span style={{ color: getRoleColor(team.role) }}>
                    {getRoleLabel(team.role)}
                  </span>
                  <ChevronDown size={16} />
                </button>
                
                <button className={styles.removeButton}>
                  Delete
                </button>
              </div>
            </div>
          ))}
          
          {filteredTeams.length === 0 && (
            <div className={styles.emptyState}>
              <User size={48} />
              <h3>No teams found</h3>
              <p>Create your first team to get started with better organization.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
